import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { FullscreenLayoutAreas, FullscreenLayoutTemplate } from '@src/assets/css/fullscreen.template.scss';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { BLACK_LINE_GUID, SUBSTORIES_VS_GUID, SYSTEM_VS_GUID } from '@src/suite/common/enums/constants';
import { AppName, SearchType, TeamScope } from '@src/suite/common/enums/enums';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { BasicContentService } from '@src/suite/common/services/basic-content-service';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { ViewStateService } from '@src/suite/common/services/view-state-service';
import {
    FourthSidecarActions,
    FullscreenActions,
    MainSidecarActions,
    OpenFullscreenModalActions,
    SecondSidecarActions,
    SidecarEfffectActions,
    ThirdSidecarActions,
} from '@src/suite/common/store/fullscreen/actions/fullscreen.actions';
import { SidecarMenu, SidecarState } from '@src/suite/common/store/fullscreen/models/fullscreen.models';
import {
    selectAllStoriesMap,
    selectCurrentRecord,
    selectCurrentRecordType,
    selectCurrentStoryParentTopic,
    selectFullscreenRecordGuid,
    selectLayoutTemplate,
} from '@src/suite/common/store/fullscreen/selectors/fullscreen.selectors';
import {
    selectAllSidecarStates,
    selectFullscreenAllStoriesSelected,
    selectSidecarCurrentRundownSelectedStory,
    selectSidecarCurrentStoryRelatives,
    selectSidecarCurrentStoryRelativesIndex,
    selectSidecarCurrentTopicSelectedAssignment,
    selectSidecarCurrentTopicSelectedStory,
    selectSidecarCurrentTopicStoryGuids,
    selectSidecarFourthCurrentAssignmentViewState,
    selectSidecarFourthCurrentRundownViewState,
    selectSidecarFourthCurrentStoryViewState,
    selectSidecarFourthCurrentTopicViewState,
    selectSidecarMainCurrentAssignmentViewState,
    selectSidecarMainCurrentRundownViewState,
    selectSidecarMainCurrentStoryViewState,
    selectSidecarMainCurrentTopicViewState,
    selectSidecarSecondCurrentAssignmentViewState,
    selectSidecarSecondCurrentRundownViewState,
    selectSidecarSecondCurrentStoryViewState,
    selectSidecarSecondCurrentTopicViewState,
    selectSidecarSelectedRundown,
    selectSidecarSelectedStoryIndex,
    selectSidecarSelectedTopic,
    selectSidecarState,
    selectSidecarStories,
    selectSidecarThirdCurrentAssignmentViewState,
    selectSidecarThirdCurrentRundownViewState,
    selectSidecarThirdCurrentStoryViewState,
    selectSidecarThirdCurrentTopicViewState,
} from '@src/suite/common/store/fullscreen/selectors/sidecar.selectors';
import { MosActions } from '@src/suite/common/store/mos/actions/mos.actions';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { AssignmentRecordActions, RecordHttpActions, RundownRecordActions, StoriesRecordActions, TopicRecordActions } from '@src/suite/common/store/records/records.actions';
import { selectRundownRecordsMap, selectSubStoriesByParentGuid, selectTopicRecordsMap } from '@src/suite/common/store/records/records.selectors';
import { StoryActions } from '@src/suite/common/store/story/actions/story.actions';
import { ncsItemRequestResponse } from '@src/suite/common/utils/mos-utlis';
import { cleanupStoryFromSidecarActions, findIfStoryVisibleInSidecar } from '@src/suite/common/utils/record-utils';
import { navigateToStory } from '@src/suite/common/utils/store-utils';
import { getDatabasesOptions, isSingleViewStateRecord } from '@src/suite/common/utils/viewstate-utils';
import { RundownService } from '@src/suite/live/services/rundown.service';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { TopicService } from '@src/suite/playbook/services/topic-service';
import { catchError, debounceTime, exhaustMap, filter, forkJoin, from, map, of, switchMap } from 'rxjs';

import { selectI18N } from '../../i18n/selectors/i18n.selectors';

@Injectable()
export class FullscreenModalSidecarEffects {
    private nomWebServicesJson = inject(NomWebServicesJson);
    private topicService = inject(TopicService);
    private actions$ = inject(Actions);
    private store = inject(Store);
    private playbookSearchService = inject(PlaybookSearchService);
    private rundownService = inject(RundownService);
    private basicContentService = inject(BasicContentService);
    private globalTablesService = inject(GlobalTablesService);
    private viewStateService = inject(ViewStateService);
    private languageSettings = this.store.selectSignal(selectI18N);

    onSidecarMenuSelected$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.selectSidecarMenu),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.selectSidecarMenu({ menu: action.menu });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.selectSidecarMenu({ menu: action.menu });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.selectSidecarMenu({ menu: action.menu });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.selectSidecarMenu({ menu: action.menu });
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    loadSidecarTopicViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.loadSidecarTopicViewStates),
            switchMap(({ sidecarType }) => {
                return this.viewStateService.loadViewStateRecords(AppName.PLAYBOOK, TeamScope.GLOBALANDUSER).pipe(
                    map(({ data }) => data?.filter(vs => vs.PSTViewStateType === ViewStateTypes.TOPIC)),
                    exhaustMap(views => {
                        const actions = [];
                        switch (sidecarType) {
                            case FullscreenLayoutAreas.SIDECAR_MAIN:
                                actions.push(MainSidecarActions.setTopicViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_SECOND:
                                actions.push(SecondSidecarActions.setTopicViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_THIRD:
                                actions.push(ThirdSidecarActions.setTopicViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                                actions.push(FourthSidecarActions.setTopicViewStates({ viewStates: views }));
                                break;
                            default:
                                break;
                        }
                        const preselectedVS = views?.[0];
                        if (preselectedVS) {
                            actions.push(SidecarEfffectActions.setSidecarTopicCurrentViewState({ guid: preselectedVS.Guid, sidecarType }));
                        }
                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(ApplicationActions.noOpAction());
                    }),
                    catchError(() => of(ApplicationActions.noOpAction()))
                );
            })
        );
    });

    setSidecarTopicViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setSidecarTopicCurrentViewState),
            map(({ guid, sidecarType }) => {
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentTopicViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentTopicViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentTopicViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentTopicViewState({ guid });
                    default:
                        break;
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    loadSidecarTopics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MainSidecarActions.setCurrentTopicViewState,
                SecondSidecarActions.setCurrentTopicViewState,
                ThirdSidecarActions.setCurrentTopicViewState,
                FourthSidecarActions.setCurrentTopicViewState,
                MainSidecarActions.reloadTopicsBasedOnCurrentViewState,
                SecondSidecarActions.reloadTopicsBasedOnCurrentViewState,
                ThirdSidecarActions.reloadTopicsBasedOnCurrentViewState,
                FourthSidecarActions.reloadTopicsBasedOnCurrentViewState
            ),
            concatLatestFrom(() => [
                this.store.select(selectSidecarMainCurrentTopicViewState),
                this.store.select(selectSidecarSecondCurrentTopicViewState),
                this.store.select(selectSidecarThirdCurrentTopicViewState),
                this.store.select(selectSidecarFourthCurrentTopicViewState),
            ]),
            switchMap(([action, main, second, third, fourth]) => {
                let viewState: StorytellingViewStateRecord = null;
                let sidecarType: FullscreenLayoutAreas = null;
                switch (action.type) {
                    case MainSidecarActions.setCurrentTopicViewState.type:
                    case MainSidecarActions.reloadTopicsBasedOnCurrentViewState.type:
                        viewState = main;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_MAIN;
                        break;

                    case SecondSidecarActions.setCurrentTopicViewState.type:
                    case SecondSidecarActions.reloadTopicsBasedOnCurrentViewState.type:
                        viewState = second;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_SECOND;
                        break;

                    case ThirdSidecarActions.setCurrentTopicViewState.type:
                    case ThirdSidecarActions.reloadTopicsBasedOnCurrentViewState.type:
                        viewState = third;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_THIRD;
                        break;

                    case FourthSidecarActions.setCurrentTopicViewState.type:
                    case FourthSidecarActions.reloadTopicsBasedOnCurrentViewState.type:
                        viewState = fourth;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_FOURTH;
                        break;

                    default:
                        break;
                }
                if (viewState?.Guid) {
                    return this.loadSidecarTopicsByVS(sidecarType, viewState);
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    reloadSidecarTopics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.reloadSidecarTopicsBasedOnCurrentViewState),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.reloadTopicsBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.reloadTopicsBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.reloadTopicsBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.reloadTopicsBasedOnCurrentViewState();
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    onSidecarTopicSearchTermChanged$ = createEffect(() => {
        return this.actions$.pipe(
            debounceTime(300),
            ofType(SidecarEfffectActions.searchTopics),
            switchMap(({ sidecarType, searchTerm }) => {
                if (!searchTerm) {
                    return of(SidecarEfffectActions.clearSearchTopics({ sidecarType }));
                }
                const vs: StorytellingSearchViewStateRecord = {
                    PSTViewStateSearchQuery: searchTerm,
                    PSTViewStateType: ViewStateTypes.TOPIC,
                };
                return this.loadSidecarTopicsByVS(sidecarType, vs);
            })
        );
    });

    clearSidecarTopicSearch$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSearchTopics),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setTopicListGuids({ topicGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setTopicListGuids({ topicGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setTopicListGuids({ topicGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setTopicListGuids({ topicGuids: [] });
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    loadSidecarTopicsByVS(sidecarType: FullscreenLayoutAreas, viewState: StorytellingViewStateRecord) {
        return from(this.playbookSearchService.getPlaybookResults(viewState)).pipe(
            map(data => data?.data),
            exhaustMap(results => {
                const actions = [];
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        actions.push(MainSidecarActions.setTopicListGuids({ topicGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        actions.push(SecondSidecarActions.setTopicListGuids({ topicGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        actions.push(ThirdSidecarActions.setTopicListGuids({ topicGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        actions.push(FourthSidecarActions.setTopicListGuids({ topicGuids: [...(results?.listGuids || [])] }));
                        break;

                    default:
                        break;
                }
                if (results?.stories) {
                    const allStoriesMap = this.store.selectSignal(selectAllStoriesMap)();
                    // Filter out stories that are already in the store
                    actions.push(StoriesRecordActions.updateStories({ records: [...(results.stories.values() || [])]?.filter(s => !allStoriesMap?.has(s.Guid)) }));
                }
                if (results?.topics) {
                    actions.push(TopicRecordActions.updateTopics({ records: [...(results.topics?.values() || [])] }));
                }
                if (results?.assignments) {
                    actions.push(AssignmentRecordActions.updateAssignments({ records: [...(results.assignments?.values() || [])] }));
                }
                return actions;
            }),
            catchError(() => of(ApplicationActions.noOpAction()))
        );
    }

    loadSidecarRundownViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.loadSidecarRundownViewStates),
            switchMap(({ sidecarType }) => {
                return from(this.viewStateService.loadViewStateRecords(AppName.LIVE, TeamScope.GLOBALANDUSER)).pipe(
                    map(({ data }) => data?.filter(vs => vs.PSTViewStateType === ViewStateTypes.ROLIST)),
                    exhaustMap(views => {
                        const actions = [];
                        switch (sidecarType) {
                            case FullscreenLayoutAreas.SIDECAR_MAIN:
                                actions.push(MainSidecarActions.setRundownViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_SECOND:
                                actions.push(SecondSidecarActions.setRundownViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_THIRD:
                                actions.push(ThirdSidecarActions.setRundownViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                                actions.push(FourthSidecarActions.setRundownViewStates({ viewStates: views }));
                                break;
                            default:
                                break;
                        }
                        const preselectedVS = views?.[0];
                        if (preselectedVS) {
                            actions.push(SidecarEfffectActions.setSidecarRundownCurrentViewState({ guid: preselectedVS.Guid, sidecarType }));
                        }
                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(ApplicationActions.noOpAction());
                    }),
                    catchError(() => of(ApplicationActions.noOpAction()))
                );
            })
        );
    });

    setSidecarRundownViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setSidecarRundownCurrentViewState),
            map(({ guid, sidecarType }) => {
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentRundownViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentRundownViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentRundownViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentRundownViewState({ guid });
                    default:
                        break;
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    loadSidecarRundowns$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MainSidecarActions.setCurrentRundownViewState,
                SecondSidecarActions.setCurrentRundownViewState,
                ThirdSidecarActions.setCurrentRundownViewState,
                FourthSidecarActions.setCurrentRundownViewState,
                MainSidecarActions.reloadRundownsBasedOnCurrentViewState,
                SecondSidecarActions.reloadRundownsBasedOnCurrentViewState,
                ThirdSidecarActions.reloadRundownsBasedOnCurrentViewState,
                FourthSidecarActions.reloadRundownsBasedOnCurrentViewState
            ),
            concatLatestFrom(() => [
                this.store.select(selectSidecarMainCurrentRundownViewState),
                this.store.select(selectSidecarSecondCurrentRundownViewState),
                this.store.select(selectSidecarThirdCurrentRundownViewState),
                this.store.select(selectSidecarFourthCurrentRundownViewState),
            ]),
            switchMap(([action, main, second, third, fourth]) => {
                let viewState: StorytellingViewStateRecord = null;
                let sidecarType: FullscreenLayoutAreas = null;
                switch (action.type) {
                    case MainSidecarActions.setCurrentRundownViewState.type:
                    case MainSidecarActions.reloadRundownsBasedOnCurrentViewState.type:
                        viewState = main;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_MAIN;
                        break;

                    case SecondSidecarActions.setCurrentRundownViewState.type:
                    case SecondSidecarActions.reloadRundownsBasedOnCurrentViewState.type:
                        viewState = second;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_SECOND;
                        break;

                    case ThirdSidecarActions.setCurrentRundownViewState.type:
                    case ThirdSidecarActions.reloadRundownsBasedOnCurrentViewState.type:
                        viewState = third;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_THIRD;
                        break;

                    case FourthSidecarActions.setCurrentRundownViewState.type:
                    case FourthSidecarActions.reloadRundownsBasedOnCurrentViewState.type:
                        viewState = fourth;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_FOURTH;
                        break;

                    default:
                        break;
                }
                if (viewState?.Guid) {
                    return this.loadSidecarRundownsByVS(sidecarType, viewState);
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    reloadSidecarRundowns$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.reloadSidecarRundownsBasedOnCurrentViewState),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.reloadRundownsBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.reloadRundownsBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.reloadRundownsBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.reloadRundownsBasedOnCurrentViewState();
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    onSidecarRundownSearchTermChanged$ = createEffect(() => {
        return this.actions$.pipe(
            debounceTime(300),
            ofType(SidecarEfffectActions.searchRundowns),
            switchMap(({ sidecarType, searchTerm }) => {
                if (!searchTerm) {
                    return of(SidecarEfffectActions.clearSearchRundowns({ sidecarType }));
                }
                const vs: StorytellingSearchViewStateRecord = {
                    PSTViewStateSearchQuery: searchTerm,
                    PSTViewStateType: ViewStateTypes.ROLIST,
                };
                return this.loadSidecarRundownsByVS(sidecarType, vs);
            })
        );
    });

    clearSidecarRundownSearch$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSearchRundowns),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setRundownListGuids({ rundownGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setRundownListGuids({ rundownGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setRundownListGuids({ rundownGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setRundownListGuids({ rundownGuids: [] });
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    openCurrentRundown$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.openCurrentRundown),
            concatLatestFrom(() => [this.store.select(selectRundownRecordsMap)]),
            switchMap(([{ recordGuid, sidecarMenu }, rundowns]) => {
                const storeRecord = rundowns.get(recordGuid);
                if (!storeRecord) {
                    return of(ApplicationActions.noOpAction());
                }
                return this.rundownService.getRundown(storeRecord.Database, storeRecord.Path.fullPath, storeRecord.Guid).pipe(
                    filter(res => res.status),
                    map(res => res.data),
                    exhaustMap(ro => {
                        const actions = [];
                        actions.push(RundownRecordActions.updateRundowns({ records: [ro] }));
                        actions.push(StoriesRecordActions.updateStories({ records: (ro as StorytellingRundownRecord).children }));
                        switch (sidecarMenu) {
                            case FullscreenLayoutAreas.SIDECAR_MAIN:
                                actions.push(MainSidecarActions.selectSidecarRundown({ guid: recordGuid }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_SECOND:
                                actions.push(SecondSidecarActions.selectSidecarRundown({ guid: recordGuid }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_THIRD:
                                actions.push(ThirdSidecarActions.selectSidecarRundown({ guid: recordGuid }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                                actions.push(FourthSidecarActions.selectSidecarRundown({ guid: recordGuid }));
                                break;
                        }

                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(ApplicationActions.noOpAction());
                    }),
                    catchError(() => of(ApplicationActions.noOpAction()))
                );
            })
        );
    });

    clearSelectedRundown$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSelectedRundown),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.selectSidecarRundown({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.selectSidecarRundown({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.selectSidecarRundown({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.selectSidecarRundown({ guid: null });
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    loadSidecarRundownsByVS(sidecarType: FullscreenLayoutAreas, viewState: StorytellingViewStateRecord) {
        if (isSingleViewStateRecord(viewState)) {
            const databases = getDatabasesOptions(
                viewState,
                this.nomWebServicesJson.PSTServer,
                this.nomWebServicesJson.HelloResults.HomeProgram,
                this.globalTablesService.AllGroups,
                this.globalTablesService.AllGroupsArray
            );

            return forkJoin(
                databases.map(database => {
                    return from(this.rundownService.loadRundownsV2Observable(viewState, database));
                })
            ).pipe(
                exhaustMap(response => {
                    let rundowns = new Map<string, StorytellingRundownRecord>();
                    let stories = new Map<string, StorytellingStoryRecord>();
                    let listGuids = new Set<string>();
                    response.forEach(res => {
                        if (res.status) {
                            rundowns = new Map([...rundowns, ...res.data.rundowns]);
                            stories = new Map([...stories, ...res.data.stories]);
                            listGuids = new Set([...listGuids, ...res.data.listGuids]);
                        }
                    });

                    const actions = [];
                    const listRundownGuids = Array.from(listGuids);
                    switch (sidecarType) {
                        case FullscreenLayoutAreas.SIDECAR_MAIN:
                            actions.push(MainSidecarActions.setRundownListGuids({ rundownGuids: [...(listRundownGuids || [])] }));
                            break;

                        case FullscreenLayoutAreas.SIDECAR_SECOND:
                            actions.push(SecondSidecarActions.setRundownListGuids({ rundownGuids: [...(listRundownGuids || [])] }));
                            break;

                        case FullscreenLayoutAreas.SIDECAR_THIRD:
                            actions.push(ThirdSidecarActions.setRundownListGuids({ rundownGuids: [...(listRundownGuids || [])] }));
                            break;

                        case FullscreenLayoutAreas.SIDECAR_FOURTH:
                            actions.push(FourthSidecarActions.setRundownListGuids({ rundownGuids: [...(listRundownGuids || [])] }));
                            break;

                        default:
                            break;
                    }

                    if (stories) {
                        actions.push(StoriesRecordActions.updateStories({ records: [...(stories.values() || [])] }));
                    }
                    if (rundowns) {
                        actions.push(RundownRecordActions.updateRundowns({ records: [...(rundowns?.values() || [])] }));
                    }
                    return actions;
                }),
                catchError(err => {
                    console.error(err);
                    return of(ApplicationActions.noOpAction());
                })
            );
        }
    }

    // assignment sidecar effects
    loadSidecarAssignmentViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.loadSidecarAssignmentViewStates),
            concatLatestFrom(() => [this.store.select(selectCurrentRecordType)]),
            switchMap(([{ sidecarType }, currentRecordType]) => {
                return from(this.viewStateService.loadViewStateRecords(AppName.PLAYBOOK, TeamScope.GLOBALANDUSER)).pipe(
                    map(({ data }) => data?.filter(vs => vs.PSTViewStateType === ViewStateTypes.ASSIGNMENT)),
                    exhaustMap(viewsData => {
                        const actions = [];
                        let views = [...viewsData];
                        if (currentRecordType !== ObjectTypes.RUNDOWN) {
                            const systemVs: StorytellingViewStateRecord = {
                                Guid: SYSTEM_VS_GUID,
                                PSTViewStateType: ViewStateTypes.ASSIGNMENT,
                                Title: this.languageSettings().PSTWebCurrentTopicLabel,
                            };

                            views = [systemVs, ...views];
                        }

                        switch (sidecarType) {
                            case FullscreenLayoutAreas.SIDECAR_MAIN:
                                actions.push(MainSidecarActions.setAssignmentViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_SECOND:
                                actions.push(SecondSidecarActions.setAssignmentViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_THIRD:
                                actions.push(ThirdSidecarActions.setAssignmentViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                                actions.push(FourthSidecarActions.setAssignmentViewStates({ viewStates: views }));
                                break;
                            default:
                                break;
                        }
                        const preselectedVS = views?.[0];
                        if (preselectedVS) {
                            actions.push(SidecarEfffectActions.setSidecarAssignmentCurrentViewState({ guid: preselectedVS.Guid, sidecarType }));
                        }
                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(ApplicationActions.noOpAction());
                    }),
                    catchError(() => of(ApplicationActions.noOpAction()))
                );
            })
        );
    });

    setSidecarAssignmentViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setSidecarAssignmentCurrentViewState),
            map(({ guid, sidecarType }) => {
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentAssignmentViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentAssignmentViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentAssignmentViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentAssignmentViewState({ guid });
                    default:
                        break;
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    loadSidecarAssignments$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MainSidecarActions.setCurrentAssignmentViewState,
                SecondSidecarActions.setCurrentAssignmentViewState,
                ThirdSidecarActions.setCurrentAssignmentViewState,
                FourthSidecarActions.setCurrentAssignmentViewState,
                MainSidecarActions.reloadAssignmentBasedOnCurrentViewState,
                SecondSidecarActions.reloadAssignmentBasedOnCurrentViewState,
                ThirdSidecarActions.reloadAssignmentBasedOnCurrentViewState,
                FourthSidecarActions.reloadAssignmentBasedOnCurrentViewState
            ),
            concatLatestFrom(() => [
                this.store.select(selectSidecarMainCurrentAssignmentViewState),
                this.store.select(selectSidecarSecondCurrentAssignmentViewState),
                this.store.select(selectSidecarThirdCurrentAssignmentViewState),
                this.store.select(selectSidecarFourthCurrentAssignmentViewState),
            ]),
            switchMap(([action, main, second, third, fourth]) => {
                let viewState: StorytellingViewStateRecord = null;
                let sidecarType: FullscreenLayoutAreas = null;
                switch (action.type) {
                    case MainSidecarActions.setCurrentAssignmentViewState.type:
                    case MainSidecarActions.reloadAssignmentBasedOnCurrentViewState.type:
                        viewState = main;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_MAIN;
                        break;

                    case SecondSidecarActions.setCurrentAssignmentViewState.type:
                    case SecondSidecarActions.reloadAssignmentBasedOnCurrentViewState.type:
                        viewState = second;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_SECOND;
                        break;

                    case ThirdSidecarActions.setCurrentAssignmentViewState.type:
                    case ThirdSidecarActions.reloadAssignmentBasedOnCurrentViewState.type:
                        viewState = third;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_THIRD;
                        break;

                    case FourthSidecarActions.setCurrentAssignmentViewState.type:
                    case FourthSidecarActions.reloadAssignmentBasedOnCurrentViewState.type:
                        viewState = fourth;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_FOURTH;
                        break;

                    default:
                        break;
                }
                if (viewState?.Guid) {
                    return this.loadSidecarAssignmentsByVS(sidecarType, viewState);
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    reloadSidecarAssignments$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.reloadSidecarAssignmentBasedOnCurrentViewState),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.reloadAssignmentBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.reloadAssignmentBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.reloadAssignmentBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.reloadAssignmentBasedOnCurrentViewState();
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    onSidecarAssignmentSearchTermChanged$ = createEffect(() => {
        return this.actions$.pipe(
            debounceTime(300),
            ofType(SidecarEfffectActions.searchAssignments),
            switchMap(({ sidecarType, searchTerm }) => {
                if (!searchTerm) {
                    return of(SidecarEfffectActions.clearSearchAssignment({ sidecarType }));
                }
                const vs: StorytellingSearchViewStateRecord = {
                    PSTViewStateSearchQuery: searchTerm,
                    PSTViewStateType: ViewStateTypes.ASSIGNMENT,
                };
                return this.loadSidecarAssignmentsByVS(sidecarType, vs);
            })
        );
    });

    clearSidecarAssignmentSearch$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSearchAssignment),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setAssignmentListGuids({ assignmentGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setAssignmentListGuids({ assignmentGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setAssignmentListGuids({ assignmentGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setAssignmentListGuids({ assignmentGuids: [] });
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    clearSelectedAssignment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSelectedAssignment),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.selectSidecarAssignment({ assignmentGuid: null });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.selectSidecarAssignment({ assignmentGuid: null });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.selectSidecarAssignment({ assignmentGuid: null });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.selectSidecarAssignment({ assignmentGuid: null });
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    setCurrentTopicAssignments$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentTopicAssignments),
            concatLatestFrom(() => [this.store.select(selectCurrentRecord), this.store.select(selectCurrentStoryParentTopic)]),
            map(([action, currentRecord, storyParentTopic]) => {
                const record = currentRecord?.Type === ObjectTypes.TOPIC ? currentRecord : storyParentTopic;
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setAssignmentListGuids({ assignmentGuids: [...((record as StorytellingTopicRecord)?.Sequence || [])] });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setAssignmentListGuids({ assignmentGuids: [...((record as StorytellingTopicRecord)?.Sequence || [])] });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setAssignmentListGuids({ assignmentGuids: [...((record as StorytellingTopicRecord)?.Sequence || [])] });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setAssignmentListGuids({ assignmentGuids: [...((record as StorytellingTopicRecord)?.Sequence || [])] });
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    setAssignmentsListSearchType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setAssignmentsListSearchType),
            map(({ sidecarType, searchType }) => {
                if (!Object.values(SearchType).includes(searchType)) {
                    return ApplicationActions.noOpAction();
                }
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setAssignmentsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setAssignmentsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setAssignmentsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setAssignmentsListSearchType({ searchType });
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    setStoriesListSearchType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setStoriesListSearchType),
            map(({ sidecarType, searchType }) => {
                if (!Object.values(SearchType).includes(searchType)) {
                    return ApplicationActions.noOpAction();
                }
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setStoriesListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setStoriesListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setStoriesListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setStoriesListSearchType({ searchType });
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    setTopicsListSearchType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setTopicsListSearchType),
            map(({ sidecarType, searchType }) => {
                if (!Object.values(SearchType).includes(searchType)) {
                    return ApplicationActions.noOpAction();
                }
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setTopicsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setTopicsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setTopicsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setTopicsListSearchType({ searchType });
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    setRundownsListSearchType$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setRundownsListSearchType),
            map(({ sidecarType, searchType }) => {
                if (!Object.values(SearchType).includes(searchType)) {
                    return ApplicationActions.noOpAction();
                }
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setRundownsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setRundownsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setRundownsListSearchType({ searchType });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setRundownsListSearchType({ searchType });
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    loadSidecarAssignmentsByVS(sidecarType: FullscreenLayoutAreas, viewState: StorytellingViewStateRecord) {
        if (viewState?.Guid === SYSTEM_VS_GUID) {
            return of(SidecarEfffectActions.setCurrentTopicAssignments({ sidecarType }));
        }
        return from(this.playbookSearchService.getPlaybookResults(viewState)).pipe(
            map(data => data?.data),
            exhaustMap(results => {
                const actions = [];
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        actions.push(MainSidecarActions.setAssignmentListGuids({ assignmentGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        actions.push(SecondSidecarActions.setAssignmentListGuids({ assignmentGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        actions.push(ThirdSidecarActions.setAssignmentListGuids({ assignmentGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        actions.push(FourthSidecarActions.setAssignmentListGuids({ assignmentGuids: [...(results?.listGuids || [])] }));
                        break;

                    default:
                        break;
                }
                if (results?.topics) {
                    actions.push(TopicRecordActions.updateTopics({ records: [...(results.topics?.values() || [])] }));
                }
                if (results?.assignments) {
                    actions.push(AssignmentRecordActions.updateAssignments({ records: [...(results.assignments?.values() || [])] }));
                }
                return actions;
            }),
            catchError(() => of(ApplicationActions.noOpAction()))
        );
    }
    // Eof assignment sidecar effects

    // stories sidecar effects
    loadSidecarStoryViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.loadSidecarStoryViewStates),
            concatLatestFrom(() => [this.store.select(selectCurrentRecordType)]),
            switchMap(([{ sidecarType }, currentRecordType]) => {
                return from(this.viewStateService.loadViewStateRecords(AppName.PLAYBOOK, TeamScope.GLOBALANDUSER)).pipe(
                    map(({ data }) => data?.filter(vs => vs.PSTViewStateType === ViewStateTypes.SCRIPT)),
                    exhaustMap(viewsData => {
                        const actions = [];
                        const systemVs: StorytellingViewStateRecord = {
                            Guid: SYSTEM_VS_GUID,
                            PSTViewStateType: ViewStateTypes.SCRIPT,
                            Title: currentRecordType === ObjectTypes.RUNDOWN ? this.languageSettings().PSTWebCurrentRundownLabel : this.languageSettings().PSTWebCurrentTopicLabel,
                        };

                        const subStoriesVs: StorytellingViewStateRecord = {
                            Guid: SUBSTORIES_VS_GUID,
                            PSTViewStateType: ViewStateTypes.SCRIPT,
                            Title: this.languageSettings().PSTWebSubStoriesLabel,
                        };

                        const views = [systemVs, subStoriesVs, ...viewsData];
                        switch (sidecarType) {
                            case FullscreenLayoutAreas.SIDECAR_MAIN:
                                actions.push(MainSidecarActions.setStoryViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_SECOND:
                                actions.push(SecondSidecarActions.setStoryViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_THIRD:
                                actions.push(ThirdSidecarActions.setStoryViewStates({ viewStates: views }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                                actions.push(FourthSidecarActions.setStoryViewStates({ viewStates: views }));
                                break;
                            default:
                                break;
                        }
                        const preselectedVS = views?.[0];
                        const sidecarState = this.store.selectSignal(selectSidecarState(sidecarType))();
                        if (!sidecarState?.currentStoryViewStateGuid) {
                            if (preselectedVS) {
                                actions.push(SidecarEfffectActions.setSidecarStoryCurrentViewState({ guid: preselectedVS.Guid, sidecarType }));
                            }
                        } else {
                            const existingVS = views.find(vs => vs.Guid === sidecarState.currentStoryViewStateGuid);
                            if (existingVS) {
                                actions.push(SidecarEfffectActions.setSidecarStoryCurrentViewState({ guid: existingVS.Guid, sidecarType }));
                            }
                        }
                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(ApplicationActions.noOpAction());
                    }),
                    catchError(() => of(ApplicationActions.noOpAction()))
                );
            })
        );
    });

    setSidecarStoryViewState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setSidecarStoryCurrentViewState),
            map(({ guid, sidecarType }) => {
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentStoryViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentStoryViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentStoryViewState({ guid });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentStoryViewState({ guid });
                    default:
                        break;
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    loadSidecarStories$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MainSidecarActions.setCurrentStoryViewState,
                SecondSidecarActions.setCurrentStoryViewState,
                ThirdSidecarActions.setCurrentStoryViewState,
                FourthSidecarActions.setCurrentStoryViewState,
                MainSidecarActions.reloadStoryBasedOnCurrentViewState,
                SecondSidecarActions.reloadStoryBasedOnCurrentViewState,
                ThirdSidecarActions.reloadStoryBasedOnCurrentViewState,
                FourthSidecarActions.reloadStoryBasedOnCurrentViewState
            ),
            concatLatestFrom(() => [
                this.store.select(selectSidecarMainCurrentStoryViewState),
                this.store.select(selectSidecarSecondCurrentStoryViewState),
                this.store.select(selectSidecarThirdCurrentStoryViewState),
                this.store.select(selectSidecarFourthCurrentStoryViewState),
                this.store.select(selectCurrentRecordType),
            ]),
            switchMap(([action, main, second, third, fourth, currentRecordType]) => {
                let viewState: StorytellingViewStateRecord = null;
                let sidecarType: FullscreenLayoutAreas = null;
                switch (action.type) {
                    case MainSidecarActions.setCurrentStoryViewState.type:
                    case MainSidecarActions.reloadStoryBasedOnCurrentViewState.type:
                        viewState = main;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_MAIN;
                        break;

                    case SecondSidecarActions.setCurrentStoryViewState.type:
                    case SecondSidecarActions.reloadStoryBasedOnCurrentViewState.type:
                        viewState = second;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_SECOND;
                        break;

                    case ThirdSidecarActions.setCurrentStoryViewState.type:
                    case ThirdSidecarActions.reloadStoryBasedOnCurrentViewState.type:
                        viewState = third;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_THIRD;
                        break;

                    case FourthSidecarActions.setCurrentStoryViewState.type:
                    case FourthSidecarActions.reloadStoryBasedOnCurrentViewState.type:
                        viewState = fourth;
                        sidecarType = FullscreenLayoutAreas.SIDECAR_FOURTH;
                        break;

                    default:
                        break;
                }
                if (viewState?.Guid) {
                    return this.loadSidecarStoriesByVS(sidecarType, viewState, currentRecordType);
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    reloadSidecarStorys$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.reloadSidecarStoryBasedOnCurrentViewState),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.reloadStoryBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.reloadStoryBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.reloadStoryBasedOnCurrentViewState();
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.reloadStoryBasedOnCurrentViewState();
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    openCurrentTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.openCurrentTopic),
            concatLatestFrom(() => [this.store.select(selectTopicRecordsMap)]),
            switchMap(([{ recordGuid, sidecarMenu }, topics]) => {
                const storeRecord = topics.get(recordGuid);
                if (!storeRecord) {
                    return of(ApplicationActions.noOpAction());
                }
                const loadCurrentTopicRecord = async (record: StorytellingTopicRecord) => {
                    const topicRes = await this.topicService.getTopicContent(record?.Database, record?.Path?.fullPath, record?.Guid);
                    if (topicRes?.status) {
                        const topic = topicRes.data;
                        const storiesRes = await this.topicService.getTopicStories(topic?.Database, topic?.Path?.fullPath, topic?.Guid);
                        if (storiesRes?.data?.length) {
                            topic.stories = storiesRes.data;
                        }
                        return topic;
                    }
                    return null;
                };

                return from(loadCurrentTopicRecord(storeRecord)).pipe(
                    exhaustMap(rec => {
                        const actions = [];
                        actions.push(TopicRecordActions.updateTopics({ records: [rec] }));
                        if (rec?.stories?.length > 0) {
                            actions.push(StoriesRecordActions.updateStories({ records: rec?.stories }));
                        }

                        switch (sidecarMenu) {
                            case FullscreenLayoutAreas.SIDECAR_MAIN:
                                actions.push(MainSidecarActions.selectSidecarTopic({ guid: recordGuid }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_SECOND:
                                actions.push(SecondSidecarActions.selectSidecarTopic({ guid: recordGuid }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_THIRD:
                                actions.push(ThirdSidecarActions.selectSidecarTopic({ guid: recordGuid }));
                                break;
                            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                                actions.push(FourthSidecarActions.selectSidecarTopic({ guid: recordGuid }));
                                break;
                        }

                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    clearSelectedTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSelectedTopic),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.selectSidecarTopic({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.selectSidecarTopic({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.selectSidecarTopic({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.selectSidecarTopic({ guid: null });
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    onSidecarStorySearchTermChanged$ = createEffect(() => {
        return this.actions$.pipe(
            debounceTime(300),
            ofType(SidecarEfffectActions.searchStories),
            switchMap(({ sidecarType, searchTerm }) => {
                if (!searchTerm) {
                    return of(SidecarEfffectActions.clearSearchStory({ sidecarType }));
                }
                const vs: StorytellingSearchViewStateRecord = {
                    PSTViewStateType: ViewStateTypes.SCRIPT,
                    PSTViewStateSearchQuery: searchTerm,
                };
                return this.loadSidecarStoriesByVS(sidecarType, vs);
            })
        );
    });

    clearSidecarStorySearch$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSearchStory),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setStoryListGuids({ storyGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setStoryListGuids({ storyGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setStoryListGuids({ storyGuids: [] });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setStoryListGuids({ storyGuids: [] });
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    clearSelectedStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.clearSelectedStory),
            map(action => {
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.selectSidecarStory({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.selectSidecarStory({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.selectSidecarStory({ guid: null });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.selectSidecarStory({ guid: null });
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    setCurrentTopicStories$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentTopicStories),
            concatLatestFrom(() => [this.store.select(selectCurrentRecord), this.store.select(selectCurrentStoryParentTopic)]),
            map(([action, currentRecord, storyParentTopic]) => {
                const record = currentRecord?.Type === ObjectTypes.TOPIC ? currentRecord : storyParentTopic;
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setStoryListGuids({ storyGuids: [...((record as StorytellingTopicRecord)?.stories?.map(story => story?.Guid) || [])] });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setStoryListGuids({ storyGuids: [...((record as StorytellingTopicRecord)?.stories?.map(story => story?.Guid) || [])] });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setStoryListGuids({ storyGuids: [...((record as StorytellingTopicRecord)?.stories?.map(story => story?.Guid) || [])] });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setStoryListGuids({ storyGuids: [...((record as StorytellingTopicRecord)?.stories?.map(story => story?.Guid) || [])] });
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    setCurrentSubStories$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentSubStories),
            map(action => {
                let parentGuid = action.parentGuid;
                if (!parentGuid) {
                    const currentRecord = this.store.selectSignal(selectCurrentRecord)() as StorytellingStoryRecord;
                    parentGuid = currentRecord?.PSTPrimaryStoryGUID || currentRecord?.Guid;
                }

                const subStoriesMap = this.store.selectSignal(selectSubStoriesByParentGuid)();
                const subStories = subStoriesMap?.get(parentGuid) || [];
                const storyGuids = subStories.map(s => s.Guid);
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setStoryListGuids({ storyGuids });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setStoryListGuids({ storyGuids });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setStoryListGuids({ storyGuids });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setStoryListGuids({ storyGuids });
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    openSubStoriesInSidecar$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.openSubStoriesInSidecar),
            exhaustMap(action => {
                const { sidecarType, parentGuid } = action;
                const actions: Action[] = [
                    SidecarEfffectActions.clearSelectedStory({ sidecarType: sidecarType }),
                    SidecarEfffectActions.setStoriesListSearchType({ searchType: SearchType.SAVED, sidecarType }),
                    SidecarEfffectActions.selectSidecarMenu({ menu: SidecarMenu.STORIES, sidecarType }),
                    SidecarEfffectActions.setSidecarStoryCurrentViewState({ guid: SUBSTORIES_VS_GUID, sidecarType }),
                    SidecarEfffectActions.setCurrentSubStories({ sidecarType, parentGuid }),
                ];
                // Sync other sidecars that are also showing sub-stories
                if (parentGuid) {
                    const allStates = this.store.selectSignal(selectAllSidecarStates)();
                    for (const [key, state] of Object.entries(allStates)) {
                        if (key !== sidecarType && state?.currentStoryViewStateGuid === SUBSTORIES_VS_GUID) {
                            actions.push(SidecarEfffectActions.setCurrentSubStories({ sidecarType: key as FullscreenLayoutAreas, parentGuid }));
                        }
                    }
                }
                return actions;
            })
        );
    });

    openStoryFullscreenWithSubStories$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.openStoryFullscreenWithSubStories),
            exhaustMap(action => {
                const sidecarType = FullscreenLayoutAreas.SIDECAR_MAIN;
                return [
                    OpenFullscreenModalActions.openStoryFullscreenDialog({
                        recordGuid: action.recordGuid,
                        recordType: action.recordType,
                        listGuid: null,
                        standaloneStory: false,
                    }),
                    FullscreenActions.setFullscreenTemplate({ template: FullscreenLayoutTemplate.BASIC }),
                    SidecarEfffectActions.openSubStoriesInSidecar({ sidecarType, parentGuid: action.parentGuid }),
                ];
            })
        );
    });

    setCurrentROStories$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentROStories),
            concatLatestFrom(() => [this.store.select(selectCurrentRecord), this.store.select(selectCurrentStoryParentTopic)]),
            map(([action, currentRecord, storyParentTopic]) => {
                const record = currentRecord?.Type === ObjectTypes.RUNDOWN ? currentRecord : storyParentTopic;
                const storyGuids = [...((record as StorytellingRundownRecord)?.Sequence || [])].filter(guid => !!guid && guid !== '0');
                switch (action.sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setStoryListGuids({ storyGuids });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setStoryListGuids({ storyGuids });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setStoryListGuids({ storyGuids });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setStoryListGuids({ storyGuids });
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    loadSidecarStoriesByVS(sidecarType: FullscreenLayoutAreas, viewState: StorytellingViewStateRecord, currentRecordType?: ObjectTypes) {
        if (viewState?.Guid === SYSTEM_VS_GUID) {
            switch (currentRecordType) {
                case ObjectTypes.TOPIC:
                case ObjectTypes.SCRIPT:
                    return of(SidecarEfffectActions.setCurrentTopicStories({ sidecarType }));
                case ObjectTypes.RUNDOWN:
                    return of(SidecarEfffectActions.setCurrentROStories({ sidecarType }));
                default:
                    return of(ApplicationActions.noOpAction());
            }
        }
        if (viewState?.Guid === SUBSTORIES_VS_GUID) {
            const allStates = this.store.selectSignal(selectAllSidecarStates)();
            const parentGuid = allStates[sidecarType]?.subStoriesParentGuid || Object.values(allStates).find(s => s?.subStoriesParentGuid)?.subStoriesParentGuid || undefined;
            return of(SidecarEfffectActions.setCurrentSubStories({ sidecarType, parentGuid }));
        }
        return from(this.playbookSearchService.getPlaybookResults(viewState)).pipe(
            map(data => data?.data),
            exhaustMap(results => {
                const actions = [];
                switch (sidecarType) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        actions.push(MainSidecarActions.setStoryListGuids({ storyGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        actions.push(SecondSidecarActions.setStoryListGuids({ storyGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        actions.push(ThirdSidecarActions.setStoryListGuids({ storyGuids: [...(results?.listGuids || [])] }));
                        break;

                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        actions.push(FourthSidecarActions.setStoryListGuids({ storyGuids: [...(results?.listGuids || [])] }));
                        break;

                    default:
                        break;
                }
                if (results?.topics) {
                    actions.push(TopicRecordActions.updateTopics({ records: [...(results.topics?.values() || [])] }));
                }
                if (results?.stories) {
                    actions.push(StoriesRecordActions.updateStories({ records: [...(results.stories?.values() || [])] }));
                }
                return actions;
            }),
            catchError(() => of(ApplicationActions.noOpAction()))
        );
    }

    openStoryInSidecarMenuAction = (sidecarType: FullscreenLayoutAreas, sidecarMenu: SidecarMenu, guid: string): Action => {
        switch (sidecarType) {
            case FullscreenLayoutAreas.SIDECAR_MAIN:
                switch (sidecarMenu) {
                    case SidecarMenu.STORIES:
                        return MainSidecarActions.selectSidecarStory({ guid: guid });
                    case SidecarMenu.TOPICS:
                        return MainSidecarActions.setCurrentTopicSelectedStory({ guid: guid });
                    case SidecarMenu.RUNDOWNS:
                        return MainSidecarActions.setCurrentROSelectedStory({ guid: guid });
                    default:
                        return MainSidecarActions.selectSidecarStory({ guid: guid });
                }
            case FullscreenLayoutAreas.SIDECAR_SECOND:
                switch (sidecarMenu) {
                    case SidecarMenu.STORIES:
                        return SecondSidecarActions.selectSidecarStory({ guid: guid });
                    case SidecarMenu.TOPICS:
                        return SecondSidecarActions.setCurrentTopicSelectedStory({ guid: guid });
                    case SidecarMenu.RUNDOWNS:
                        return SecondSidecarActions.setCurrentROSelectedStory({ guid: guid });
                    default:
                        return SecondSidecarActions.selectSidecarStory({ guid: guid });
                }
            case FullscreenLayoutAreas.SIDECAR_THIRD:
                switch (sidecarMenu) {
                    case SidecarMenu.STORIES:
                        return ThirdSidecarActions.selectSidecarStory({ guid: guid });
                    case SidecarMenu.TOPICS:
                        return ThirdSidecarActions.setCurrentTopicSelectedStory({ guid: guid });
                    case SidecarMenu.RUNDOWNS:
                        return ThirdSidecarActions.setCurrentROSelectedStory({ guid: guid });
                    default:
                        return ThirdSidecarActions.selectSidecarStory({ guid: guid });
                }
            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                switch (sidecarMenu) {
                    case SidecarMenu.STORIES:
                        return FourthSidecarActions.selectSidecarStory({ guid: guid });
                    case SidecarMenu.TOPICS:
                        return FourthSidecarActions.setCurrentTopicSelectedStory({ guid: guid });
                    case SidecarMenu.RUNDOWNS:
                        return FourthSidecarActions.setCurrentROSelectedStory({ guid: guid });
                    default:
                        return FourthSidecarActions.selectSidecarStory({ guid: guid });
                }
            default:
                return ApplicationActions.noOpAction();
        }
    };

    openStoryInSidecar$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.openStoryInSidecar),
            concatLatestFrom(() => [this.store.select(selectAllStoriesMap)]),
            switchMap(([{ guid, sidecarType, sidecarMenu }, stories]) => {
                const storeRecord = stories.get(guid);
                if (!storeRecord) {
                    return of(ApplicationActions.noOpAction());
                }

                const sidecarStates = this.store.selectSignal(selectAllSidecarStates)();
                const fullscreenRecordGuid = this.store.selectSignal(selectFullscreenRecordGuid)();
                const sidecarActions: Action[] = [ApplicationActions.noOpAction()];

                // if the story is already in fullscreen, we need to remove it from the sidecar and focus it instead
                if (fullscreenRecordGuid === guid) {
                    sidecarActions.push(...cleanupStoryFromSidecarActions(sidecarStates, guid));
                    return of(...sidecarActions, StoryActions.focusEditor({ storyGuid: guid }));
                }

                const currentTemplate = this.store.selectSignal(selectLayoutTemplate)();
                const isOnlyMain = currentTemplate === FullscreenLayoutTemplate.ONLY_MAIN;

                // if the current template is only main, we need to switch to basic, so we can have a sidecar
                if (isOnlyMain) {
                    sidecarType = FullscreenLayoutAreas.SIDECAR_MAIN;
                    sidecarActions.push(FullscreenActions.setFullscreenTemplate({ template: FullscreenLayoutTemplate.BASIC }));
                }

                // if the sidecar menu is provided, we need to open it first
                if (sidecarMenu) {
                    sidecarActions.push(SidecarEfffectActions.selectSidecarMenu({ menu: sidecarMenu, sidecarType }));
                }

                // we concider a story is opened in a sidecar when either if the sidecar is in the current layout and the specific sidecar tab is opened
                const result = findIfStoryVisibleInSidecar(sidecarStates, guid, currentTemplate);

                let sidecar: string | undefined;
                let sidecarState: SidecarState | undefined;

                if (result) {
                    [sidecar, sidecarState] = result;
                }

                const isDesiredStoryVisible = !!sidecar;

                if (isDesiredStoryVisible) {
                    // if the story is already visible in one of the sidecars, we need to clear it from every other sidecar
                    sidecarActions.push(...cleanupStoryFromSidecarActions(sidecarStates, guid, { sidecarType: sidecar as FullscreenLayoutAreas, sidecarMenu: sidecarState.selected as SidecarMenu }));
                    // focus the story
                    sidecarActions.push(StoryActions.focusEditor({ storyGuid: guid }));
                    return sidecarActions;
                } else {
                    // clean this story guid in all the other sidecars
                    sidecarActions.push(...cleanupStoryFromSidecarActions(sidecarStates, guid));
                }

                sidecarActions.push(this.openStoryInSidecarMenuAction(sidecarType, sidecarMenu, guid));

                if (storeRecord.isNewRecord) {
                    return sidecarActions;
                }

                const allStories = this.store.selectSignal(selectFullscreenAllStoriesSelected)();
                if (allStories.includes(storeRecord.Guid)) {
                    return sidecarActions;
                }

                return from(
                    this.basicContentService.getRecord<StorytellingStoryRecord>({
                        database: storeRecord.Database || this.nomWebServicesJson.getDatabase(),
                        path: storeRecord.Path.fullPath,
                        guid: storeRecord.Guid,
                    })
                ).pipe(
                    filter(res => res.status),
                    map(res => res.data),
                    map(rec => [
                        StoriesRecordActions.updateStories({
                            records: [
                                {
                                    ...rec,
                                    loadedBasicContent: true,
                                },
                            ],
                        }),
                        ...sidecarActions,
                    ]),
                    switchMap(actions => actions)
                );
            })
        );
    });
    // Eof stories sidecar effects

    onNavigateStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.navigateStory),
            concatLatestFrom(action => {
                return [
                    this.store.select(selectSidecarStories(action.sidecarMenu)),
                    this.store.select(selectSidecarCurrentStoryRelatives(action.sidecarMenu)),
                    this.store.select(selectSidecarSelectedStoryIndex(action.sidecarMenu)),
                    this.store.select(selectSidecarCurrentStoryRelativesIndex(action.sidecarMenu)),
                ];
            }),
            exhaustMap(([action, allStoriesList, relativeStoriesList, allStoriesSelectedIdx, relativeStoriesSelectedIdx]) => {
                const actions = [];
                const selectedIdx = action.throughTopicStories ? relativeStoriesSelectedIdx : allStoriesSelectedIdx;
                const storyList = action.throughTopicStories ? relativeStoriesList : allStoriesList;

                if (selectedIdx == null || selectedIdx < 0 || storyList?.length === 0 || selectedIdx >= storyList.length) {
                    return of(ApplicationActions.noOpAction());
                }
                const sidecarStates = this.store.selectSignal(selectAllSidecarStates)();
                const currentTemplate = this.store.selectSignal(selectLayoutTemplate)();
                const fullscreenRecordGuid = this.store.selectSignal(selectFullscreenRecordGuid)();

                const story = { ...storyList[selectedIdx] };
                const nextStory = navigateToStory(action.direction, selectedIdx, storyList, sidecarStates, currentTemplate, fullscreenRecordGuid);

                if (action.saveCurrentStory) {
                    actions.push(
                        ...[
                            SidecarEfffectActions.openStoryInSidecar({ guid: nextStory.Guid, sidecarType: action.sidecarMenu }),
                            RecordHttpActions.saveRecordChanges({
                                record: story,
                                fieldsToUpdate: [
                                    { key: PSTFieldNames.Type, value: ObjectTypes.SCRIPT },
                                    { key: PSTFieldNames.PSTRichText, value: story?.text },
                                    ...(story?.TextCommands ? [{ key: PSTFieldNames.TextCommands, value: story?.TextCommands }] : []),
                                ],
                            }),
                        ]
                    );
                    return from(actions);
                }

                actions.push(SidecarEfffectActions.openStoryInSidecar({ guid: nextStory?.Guid, sidecarType: action.sidecarMenu }));

                return actions;
            })
        );
    });

    setCurrentROSelectedStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentROSelectedStory),
            map(action => {
                switch (action.sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentROSelectedStory({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentROSelectedStory({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentROSelectedStory({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentROSelectedStory({ guid: action.guid });
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    getSidecarTopicSelectedStoryAction(sidecarMenu: FullscreenLayoutAreas, guid: string) {
        switch (sidecarMenu) {
            case FullscreenLayoutAreas.SIDECAR_MAIN:
                return MainSidecarActions.setCurrentTopicSelectedStory({ guid });
            case FullscreenLayoutAreas.SIDECAR_SECOND:
                return SecondSidecarActions.setCurrentTopicSelectedStory({ guid });
            case FullscreenLayoutAreas.SIDECAR_THIRD:
                return ThirdSidecarActions.setCurrentTopicSelectedStory({ guid });
            case FullscreenLayoutAreas.SIDECAR_FOURTH:
                return FourthSidecarActions.setCurrentTopicSelectedStory({ guid });
            default:
                return ApplicationActions.noOpAction();
        }
    }

    setCurrentTopicSelectedStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentTopicSelectedStory),
            switchMap(action => {
                const storyRecord = this.store.selectSignal(selectAllStoriesMap)().get(action.guid);
                if (!storyRecord) {
                    return of(this.getSidecarTopicSelectedStoryAction(action.sidecarMenu, action.guid));
                }
                /*
                When we open story in sidecar, we need to get the story from NOM as it is in the main area in the full screen. We cannot rely on SOLR.
                TODO - Probably we have to load stories from the current topic in the sidecar when we open the topic in the sidecar in the same way
                    as we do in the main area in the full screen, because loadStories is getting the stories from NOM, instead of SOLR.
                */
                return from(
                    this.basicContentService
                        .getRecord<StorytellingStoryRecord>({
                            database: storyRecord?.Database || this.nomWebServicesJson?.getDatabase(),
                            path: storyRecord?.Path?.fullPath,
                            guid: storyRecord?.Guid,
                        })
                        .then(res => {
                            if (res?.status) {
                                return res.data;
                            }
                            return null;
                        })
                        .catch(() => null)
                ).pipe(
                    filter(res => !!res),
                    exhaustMap(res => {
                        const actions = [];
                        const stories = [res];
                        if (res) {
                            actions.push(StoriesRecordActions.updateStories({ records: stories }));
                        }

                        actions.push(this.getSidecarTopicSelectedStoryAction(action.sidecarMenu, action.guid));
                        return actions;
                    })
                );
            })
        );
    });

    setCurrentTopicSelectedAssignment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentTopicSelectedAssignment),
            map(action => {
                switch (action.sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentTopicSelectedAssignment({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentTopicSelectedAssignment({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentTopicSelectedAssignment({ guid: action.guid });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentTopicSelectedAssignment({ guid: action.guid });
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    onNavigateStoryInTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.navigateStoryInCurrentTopic),
            concatLatestFrom(action => {
                return [this.store.select(selectSidecarCurrentTopicStoryGuids(action.sidecarMenu)), this.store.select(selectSidecarCurrentTopicSelectedStory(action.sidecarMenu))];
            }),
            map(([action, currentTopicStories, lastSelectedStory]) => {
                const storyList = currentTopicStories;
                const selectedIdx = storyList?.indexOf(lastSelectedStory?.Guid);
                if (selectedIdx == null || selectedIdx < 0 || !storyList || storyList?.length === 0) {
                    return ApplicationActions.noOpAction();
                }
                let storyGuid = lastSelectedStory.Guid;
                switch (action.direction) {
                    case NavigationDirection.PREVIOUS:
                        if (selectedIdx === 0) {
                            storyGuid = storyList[storyList.length - 1];
                        } else {
                            storyGuid = storyList[selectedIdx - 1];
                        }
                        break;
                    case NavigationDirection.NEXT:
                        if (selectedIdx === storyList.length - 1) {
                            storyGuid = storyList[0];
                        } else {
                            storyGuid = storyList[selectedIdx + 1];
                        }
                        break;
                    case NavigationDirection.FIRST:
                        storyGuid = storyList[0];
                        break;
                    case NavigationDirection.LAST:
                        storyGuid = storyList[storyList.length - 1];
                        break;
                }

                return SidecarEfffectActions.setCurrentTopicSelectedStory({
                    guid: storyGuid,
                    sidecarMenu: action.sidecarMenu,
                });
            })
        );
    });

    navigateAssignmentInCurrentTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.navigateAssignmentInCurrentTopic),
            concatLatestFrom(action => {
                return [this.store.select(selectSidecarSelectedTopic(action.sidecarMenu)), this.store.select(selectSidecarCurrentTopicSelectedAssignment(action.sidecarMenu))];
            }),
            map(([action, currentTopic, lastSelectedAssignment]) => {
                const assignmentList = currentTopic?.Sequence;
                const selectedIdx = assignmentList?.indexOf(lastSelectedAssignment?.Guid);
                if (selectedIdx == null || selectedIdx < 0 || !assignmentList || assignmentList?.length === 0) {
                    return ApplicationActions.noOpAction();
                }
                let assignmentGuid = lastSelectedAssignment.Guid;
                switch (action.direction) {
                    case NavigationDirection.PREVIOUS:
                        if (selectedIdx === 0) {
                            assignmentGuid = assignmentList[assignmentList.length - 1];
                        } else {
                            assignmentGuid = assignmentList[selectedIdx - 1];
                        }
                        break;
                    case NavigationDirection.NEXT:
                        if (selectedIdx === assignmentList.length - 1) {
                            assignmentGuid = assignmentList[0];
                        } else {
                            assignmentGuid = assignmentList[selectedIdx + 1];
                        }
                        break;
                    case NavigationDirection.FIRST:
                        assignmentGuid = assignmentList[0];
                        break;
                    case NavigationDirection.LAST:
                        assignmentGuid = assignmentList[assignmentList.length - 1];
                        break;
                }

                return SidecarEfffectActions.setCurrentTopicSelectedAssignment({
                    guid: assignmentGuid,
                    sidecarMenu: action.sidecarMenu,
                });
            })
        );
    });

    onNavigateStoryInRO$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.navigateStoryInCurrentRO),
            concatLatestFrom(action => {
                return [this.store.select(selectSidecarSelectedRundown(action.sidecarMenu)), this.store.select(selectSidecarCurrentRundownSelectedStory(action.sidecarMenu))];
            }),
            map(([action, currentRO, lastSelectedStory]) => {
                const storyList = currentRO?.Sequence.filter(guid => guid !== BLACK_LINE_GUID);
                const selectedIdx = storyList?.indexOf(lastSelectedStory?.Guid);
                if (selectedIdx == null || selectedIdx < 0 || !storyList || storyList?.length === 0) {
                    return ApplicationActions.noOpAction();
                }
                let storyGuid = lastSelectedStory.Guid;
                switch (action.direction) {
                    case NavigationDirection.PREVIOUS:
                        if (selectedIdx === 0) {
                            storyGuid = storyList[storyList.length - 1];
                        } else {
                            storyGuid = storyList[selectedIdx - 1];
                        }
                        break;
                    case NavigationDirection.NEXT:
                        if (selectedIdx === storyList.length - 1) {
                            storyGuid = storyList[0];
                        } else {
                            storyGuid = storyList[selectedIdx + 1];
                        }
                        break;
                    case NavigationDirection.FIRST:
                        storyGuid = storyList[0];
                        break;
                    case NavigationDirection.LAST:
                        storyGuid = storyList[storyList.length - 1];
                        break;
                }

                return SidecarEfffectActions.setCurrentROSelectedStory({
                    guid: storyGuid,
                    sidecarMenu: action.sidecarMenu,
                });
            })
        );
    });

    setCurrentMosPlugin$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.setCurrentMosPlugin),
            map(({ sidecarMenu, mosPluginId }) => {
                switch (sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.setCurrentMosPlugin({ mosPluginId });
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.setCurrentMosPlugin({ mosPluginId });
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.setCurrentMosPlugin({ mosPluginId });
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.setCurrentMosPlugin({ mosPluginId });
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    onOpenPendingMosItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.openPendingMosItem),
            map(({ mosItemCommand, mosPluginId, sidecar }) => {
                return MosActions.mosClientCommandSend({
                    command: ncsItemRequestResponse(mosItemCommand),
                    mosPluginId,
                    sidecar,
                });
            })
        );
    });

    openMosPlugin$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.openMosItem),
            exhaustMap(action => {
                /**
                 * 1. check if the current layout is the same as the one in the action
                 * 2. get sidecar state to look at the selected mos plugin
                 * 3. If there was a sidecar with the mos plugin opened and initiated we need to send the mosItemRequestResponse to the sidecar.
                 * 4. If the plugin page is not the selected one, set it
                 * 5. Open the mos plugin
                 */

                const actions = [];

                /**
                 * 1. check if the current layout is the same as the one in the action
                 */
                const currentFullscreenLayout = this.store.selectSignal(selectLayoutTemplate)();
                if (currentFullscreenLayout !== action.layout) {
                    // if they are not the same, set the layout
                    actions.push(FullscreenActions.setFullscreenTemplate({ template: action.layout }));
                }
                /**
                 * 2. get sidecar state to look at the selected mos plugin
                 */
                const allSidecarStates = this.store.selectSignal(selectAllSidecarStates)();
                const sidecarState = allSidecarStates[action.sidecar] as SidecarState;

                const sidecarArea = {
                    sidecar: action.sidecar,
                    mosPluginId: sidecarState.selectedMosPluginID,
                    mosPluginInitiated: sidecarState.selectedMosPluginInitialized,
                };

                if (!sidecarArea.mosPluginId || sidecarArea.mosPluginId !== action.mosPluginId) {
                    // if they are not the same, set the mos plugin
                    sidecarArea.mosPluginId = action.mosPluginId;
                    sidecarArea.mosPluginInitiated = false;
                }

                /**
                 * 3. If there was a sidecar with the mos plugin opened and initiated we need to send the mosItemRequestResponse to the sidecar.
                 */
                if (sidecarArea.mosPluginInitiated) {
                    actions.push(MosActions.mosClientCommandSend({ command: ncsItemRequestResponse(action.mosItemCommand), mosPluginId: action.mosPluginId, sidecar: sidecarArea.sidecar }));
                    return actions;
                }

                /**
                 * 4. If the plugin page is not the selected one, set it
                 */
                if (sidecarState?.selected !== SidecarMenu.MOS) {
                    actions.push(SidecarEfffectActions.selectSidecarMenu({ menu: SidecarMenu.MOS, sidecarType: sidecarArea.sidecar }));
                }
                /**
                 * 5. Open the mos plugin
                 * !IMPORTANT!: This should be the only place we are dispatching this action
                 * This will open the mos plugin itself.
                 */
                actions.push(SidecarEfffectActions.openMosItemInSidecar({ mosPluginId: action.mosPluginId, mosItemCommand: action.mosItemCommand, sidecar: sidecarArea.sidecar }));
                return actions;
            })
        );
    });

    onMosStateReset$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidecarEfffectActions.resetMosState),
            map(({ sidecar: sidecarMenu }) => {
                switch (sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        return MainSidecarActions.resetMosState();
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        return SecondSidecarActions.resetMosState();
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        return ThirdSidecarActions.resetMosState();
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        return FourthSidecarActions.resetMosState();
                }
                return ApplicationActions.noOpAction();
            })
        );
    });
}

// LEFT FOR REFERENCE
// openMosPlugin$ = createEffect(() => {
//     return this.actions$.pipe(
//         ofType(SidecarEfffectActions.openMosItem),
//         exhaustMap(action => {
//             /**
//              * 1. check if the area has sidecar.
//              * 2. if not we need to find layout with sidecar in it.
//              * 3. find if that mos plugin is open in some sidecar in the current layout and check if there are several sidecars with
//              * the targeted mos plugin we need to get the one that is initiated.
//              * 4. if we don't find a sidecar with the mos plugin open, we need to set the first sidecar and mos plugin in it.
//              * 5. If there was a sidecar with the mos plugin opened and initiated we need to send the mosItemRequestResponse to the sidecar.
//              * 6. If there was not a sidecar with the mos plugin opened before that we need to open the mos page and mos plugin in it.
//              */

//             const actions = [];

//             /**
//              * 1. find if that mos plugin is open in some sidecar in the current layout and check if it is initiated.
//              */
//             const currentFullscreenLayout = this.store.selectSignal(selectLayoutTemplate)();
//             const areaWithSidecar = layoutTemplatesAreas[currentFullscreenLayout] as Set<FullscreenLayoutAreas>;
//             let sidecars = [...(areaWithSidecar?.values() || [])]?.filter(area => area !== FullscreenLayoutAreas.MAIN);

//             if (!sidecars?.length) {
//                 /**
//                  * 2. if not we need to find layout with sidecar in it.
//                  */
//                 for (const key in layoutTemplatesAreas) {
//                     const layoutTemplatesAreasValues = layoutTemplatesAreas[key] as Set<FullscreenLayoutAreas>;
//                     sidecars = [...(layoutTemplatesAreasValues?.values() || [])]?.filter(area => area !== FullscreenLayoutAreas.MAIN);

//                     if (sidecars?.length > 0) {
//                         actions.push(FullscreenActions.setFullscreenTemplate({ template: key as FullscreenLayoutTemplate }));
//                         break;
//                     }
//                 }
//             }

//             // TODO: what if no matter what we end up with sidecars.length === 0?
//             if (sidecars?.length === 0) {
//                 console.error('No sidecars found. We should never get here. :)');
//                 return of(ApplicationActions.noOpAction());
//             }

//             /**
//              *  * 3. find if that mos plugin is open in some sidecar in the current layout and check if there are several sidecars with
//              * the targeted mos plugin we need to get the one that is initiated.
//              */
//             const allSidecarStates = this.store.selectSignal(selectAllSidecarStates)();
//             const sidecarArea = sidecars.reduce(
//                 (acc, key: FullscreenLayoutAreas) => {
//                     if (acc.mosPluginInitiated) {
//                         return acc;
//                     }
//                     const sidecarState = allSidecarStates[key] as SidecarState;

//                     if (acc.mosPluginId && acc.mosPluginId === sidecarState.selectedMosPluginID) {
//                         acc.mosPluginInitiated = sidecarState.selectedMosPluginInitialized;
//                         if (acc.mosPluginInitiated) {
//                             acc.sidecar = key;
//                         }
//                     } else if (!acc.mosPluginId && sidecarState.selectedMosPluginID === action.mosPluginId) {
//                         acc.sidecar = key;
//                         acc.mosPluginId = sidecarState.selectedMosPluginID;
//                         acc.mosPluginInitiated = sidecarState.selectedMosPluginInitialized;
//                     }
//                     return acc;
//                 },
//                 {
//                     sidecar: null as FullscreenLayoutAreas,
//                     mosPluginId: '',
//                     mosPluginInitiated: false,
//                 }
//             );

//             if (!sidecarArea.sidecar || !sidecarArea.mosPluginId) {
//                 /**
//                  * 4. if we don't find a sidecar with the mos plugin open, we need to set the first sidecar and mos plugin in it.
//                  */
//                 sidecarArea.sidecar = sidecars[0];
//                 sidecarArea.mosPluginId = action.mosPluginId;
//                 sidecarArea.mosPluginInitiated = false;
//             }

//             /**
//              * 5. If there was a sidecar with the mos plugin opened and initiated we need to send the mosItemRequestResponse to the sidecar.
//              */
//             if (sidecarArea.mosPluginInitiated) {
//                 actions.push(MosActions.mosClientCommandSend({ command: ncsItemRequestResponse(action.mosItemCommand), mosPluginId: action.mosPluginId, sidecar: sidecarArea.sidecar }));
//                 return actions;
//             }

//             /**
//              * 6. If there was not a sidecar with the mos plugin opened before that we need to open the mos page and mos plugin in it.
//              */
//             if (allSidecarStates[sidecarArea.sidecar]?.selected !== SidecarMenu.MOS) {
//                 actions.push(SidecarEfffectActions.selectSidecarMenu({ menu: SidecarMenu.MOS, sidecarType: sidecarArea.sidecar }));
//             }
//             /**
//              * !IMPORTANT!: This should be the only place we are dispatching this action
//              * This will open the mos plugin itself.
//              */
//             actions.push(SidecarEfffectActions.openMosItemInSidecar({ mosPluginId: action.mosPluginId, mosItemCommand: action.mosItemCommand, sidecar: sidecarArea.sidecar }));
//             return actions;
//         })
//     );
// });
